import { useState } from 'react';

import { updateMethodAction } from '@/lib/admin-actions';
import { formatDate } from '@/components/status-timeline';
import { Badge, Button, Callout, Field, inputClass } from '@/components/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import { adminMethodsQuery } from '@/queries';

/**
 * The decoration pricing editor. AC-5.3.
 *
 * `print_methods` is the runtime source for the engine, so editing here changes
 * what every future estimate computes. It does not touch quotes already priced
 * or orders already placed, because those carry their own frozen numbers.
 *
 * Every save writes an audit row: who, when, old to new.
 */
export default function AdminMethodsPage() {
  const { methods, audit } = useSuspenseQuery(adminMethodsQuery).data;
  /**
   * Dollars for a form INPUT, not for display. Deliberately not `formatCents`.
   *
   * Every value below is a `defaultValue` on a text input that a person edits
   * and posts back. `formatCents(302000)` is "$3,020.00", and a comma and a
   * dollar sign in a number field are two things for the parser on the other
   * side to get wrong. `formatCents` belongs anywhere this value is READ rather
   * than edited.
   *
   * Named for what it is, after `job-cost.tsx` was found using a near-identical
   * helper for display, where it printed $3020.00 instead of $3,020.00 and
   * $-5.00 instead of -$5.00. R-285.
   */
  const inputDollars = (cents: number) => (cents / 100).toFixed(2);

  // Per method, because five forms share this screen and a refusal belongs
  // under the one it refused.
  const [refused, setRefused] = useState<Record<string, string | null>>({});
  const [saving, setSaving] = useState<string | null>(null);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Decoration pricing</h1>
        <p className="mt-1 max-w-[62ch] text-[15px] text-muted">
          These rates drive every estimate the studio and both apps produce.
        </p>
      </div>

      {/* R-190. `methods.length > 0` on the warning below meant an EMPTY table
          showed NOTHING: no methods, no rates, no explanation, just a heading and
          a blank change history. That is the state the client's system is in
          today, and this screen was the one place that should have said so.
          `[].every()` is true, so the only thing standing between the reader and
          the answer was the length guard. */}
      {methods.length === 0 && (
        <div className="mb-5">
          <Callout tone="danger" title="There are no decoration methods yet">
            Nothing can be quoted. The rate card has never been loaded, so screen
            print, DTG, DTF and embroidery do not exist in this system at all. The
            studio page tells customers to call until they do. Load the rate card
            with <code>npm run rates:load</code>, and they appear here.
          </Callout>
        </div>
      )}

      {/* R-172. Four methods exist and every one is switched off because no rate
          has been set. The screen showed four forms of zeroes and said nothing
          about why nothing can be quoted. */}
      {methods.every((m) => !m.active) && methods.length > 0 && (
        <div className="mb-5">
          <Callout tone="danger" title="Nothing can be quoted yet">
            Every method is switched off, because a method with no rates would quote
            decoration at nothing. Set the rates on a method below and tick
            "Offer this method", and it appears in the studio straight away. The
            studio page tells customers to call until then.
          </Callout>
        </div>
      )}

      <Callout tone="warn" title="Changes affect new estimates only">
        Quotes already priced and orders already placed keep the numbers they were given.
        The pricing vectors in the spec are the contract for the formula, not for these
        rates, so tuning a rate here does not break the test suite.
      </Callout>

      <div className="mt-8 space-y-5">
        {methods.map((m) => (
          <form
            key={m.key}
            /**
             * Submitted here rather than passed straight to the action, so a
             * REFUSAL lands on the form instead of on the error boundary.
             *
             * `action={updateMethodAction}` throws when the server refuses,
             * React has nowhere to put the throw, and the whole screen is
             * replaced by "This screen could not load its data. Something
             * failed at our end." The person wanted to switch on a method and
             * is told the application is broken. R-238.
             */
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              setSaving(m.key);
              setRefused((r) => ({ ...r, [m.key]: null }));
              updateMethodAction(new FormData(form))
                .then(() => setRefused((r) => ({ ...r, [m.key]: null })))
                .catch((err: Error) => setRefused((r) => ({ ...r, [m.key]: err.message })))
                .finally(() => setSaving(null));
            }}
            className="rounded-[10px] border border-line bg-panel p-5"
          >
            <input type="hidden" name="key" value={m.key} />

            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h2 className="text-lg font-bold tracking-[-0.02em]">{m.name}</h2>
              <span className="flex items-baseline gap-3">
                <Badge tone={m.active ? 'ok' : 'neutral'}>
                  {m.active ? 'Offered' : 'Switched off'}
                </Badge>
                <span className="font-mono text-sm text-muted">{m.key}</span>
              </span>
            </div>
            <p className="mt-1 text-[12.5px] text-muted">{m.blurb}</p>

            <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Minimum order" htmlFor={`${m.key}-moq`}>
                <input
                  id={`${m.key}-moq`}
                  name="moq"
                  inputMode="numeric"
                  defaultValue={m.moq}
                  className={`${inputClass} font-mono`}
                />
              </Field>

              <Field label="Turnaround" htmlFor={`${m.key}-turnaround`}>
                <input
                  id={`${m.key}-turnaround`}
                  name="turnaround"
                  defaultValue={m.turnaround}
                  className={inputClass}
                />
              </Field>

              <Field
                label="Print per location"
                htmlFor={`${m.key}-print`}
                hint="AUD per unit, per location."
              >
                <input
                  id={`${m.key}-print`}
                  name="print_per_location"
                  inputMode="decimal"
                  defaultValue={inputDollars(m.print_per_location_cents)}
                  className={`${inputClass} font-mono`}
                />
              </Field>

              <Field
                label="Setup per colour, per location"
                htmlFor={`${m.key}-setup-colour`}
                hint="AUD. Screen burning."
              >
                <input
                  id={`${m.key}-setup-colour`}
                  name="setup_per_colour_per_location"
                  inputMode="decimal"
                  defaultValue={inputDollars(m.setup_per_colour_per_location_cents)}
                  className={`${inputClass} font-mono`}
                />
              </Field>

              <Field
                label="Setup per location"
                htmlFor={`${m.key}-setup-loc`}
                hint="AUD. Digitising."
              >
                <input
                  id={`${m.key}-setup-loc`}
                  name="setup_per_location"
                  inputMode="decimal"
                  defaultValue={inputDollars(m.setup_per_location_cents)}
                  className={`${inputClass} font-mono`}
                />
              </Field>

              <Field
                label="Extra colour"
                htmlFor={`${m.key}-extra`}
                hint="AUD per unit beyond the first colour."
              >
                <input
                  id={`${m.key}-extra`}
                  name="extra_colour_cost"
                  inputMode="decimal"
                  defaultValue={inputDollars(m.extra_colour_cost_cents)}
                  className={`${inputClass} font-mono`}
                />
              </Field>
            </div>

            {/*
              * The switch, which the application did not have until now.
              *
              * `active` was set in exactly one place: a developer script run
              * against a rate card file. So every method sat switched off, the
              * studio quoted nothing, and the banner above told Guy to set the
              * rates, which would not have switched anything on. R-238.
              *
              * The server refuses to switch on a method whose rates are all
              * zero, and says why, because a method with no rates quotes
              * decoration at nothing.
              */}
            <label className="mt-5 flex items-center gap-2 text-[13px]">
              <input type="checkbox" name="active" defaultChecked={m.active} />
              Offer this method in the studio and on a quote
            </label>

            {refused[m.key] && (
              <p className="mt-3 rounded-[10px] border border-line-strong bg-raised p-3 text-[13px]">
                {refused[m.key]}
              </p>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-4">
              <Button type="submit" disabled={saving === m.key}>
                {saving === m.key ? 'Saving...' : `Save ${m.name}`}
              </Button>
              <p className="text-[13px] text-muted">
                Colour limit: {m.max_colours == null ? 'full colour' : m.max_colours}
              </p>
            </div>
          </form>
        ))}
      </div>

      <section className="mt-12" aria-labelledby="audit-heading">
        <h2 id="audit-heading" className="mb-4 text-lg font-bold tracking-[-0.02em]">
          Change history
        </h2>
        {audit.length === 0 ? (
          <p className="rounded-[10px] border border-dashed border-line-strong px-5 py-8 text-center text-[15px] text-muted">
            No rate changes recorded yet.
          </p>
        ) : (
          <ul className="divide-y divide-line overflow-hidden rounded-[10px] border border-line bg-panel">
            {audit.map((row) => (
              <li key={row.id} className="px-4 py-3.5">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-[15px] font-semibold">
                    {row.actor} changed <span className="font-mono">{row.pk}</span>
                  </p>
                  <span className="text-sm text-muted">{formatDate(row.created_at)}</span>
                </div>
                <p className="mt-1 break-all font-mono text-[13px] text-muted">
                  {JSON.stringify(row.old_value)} to {JSON.stringify(row.new_value)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
